import { Component, Fragment, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  label: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** Bumped on "Try again" so the wrapped subtree fully remounts and does not re-throw immediately. */
  resetKey: number;
}

/** Isolates crashes in Write / Create / Code so the rest of Studio keeps running. */
export class StudioModeErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, resetKey: 0 };

  static getDerivedStateFromError(error: Error): Pick<State, "hasError" | "error"> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error(`[StudioModeErrorBoundary:${this.props.label}]`, error, info.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center gap-4 px-6 py-16"
          style={{ background: "#FAFAFB" }}
        >
          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl text-xl font-black text-white"
            style={{ background: "linear-gradient(135deg, #6366F1, #3B82F6)" }}
            aria-hidden
          >
            V
          </div>
          <p className="text-center text-sm font-medium text-slate-800">This section stopped loading</p>
          <p className="max-w-sm text-center text-xs text-slate-700">
            Switch tabs and come back, or reload the page. If it keeps happening, email{" "}
            <a href="mailto:support@vinciwriter.com" className="underline">
              support@vinciwriter.com
            </a>
            .
          </p>
          <button
            type="button"
            onClick={() =>
              this.setState({
                hasError: false,
                error: null,
                resetKey: this.state.resetKey + 1,
              })
            }
            className="rounded-lg px-4 py-2 text-xs font-semibold text-white"
            style={{ background: "linear-gradient(135deg, #6366F1, #3B82F6)" }}
          >
            Reload this section
          </button>
        </div>
      );
    }
    return <Fragment key={this.state.resetKey}>{this.props.children}</Fragment>;
  }
}
